// Imperative SVG drawing helpers ported from sample-v2/app.js.
// Each draw* function clears the given <svg> element and renders into it.
// Kept imperative (DOM-mutating) on purpose — preserves the look exactly.

const SVGNS = "http://www.w3.org/2000/svg";

function svgEl(tag: string, attrs: Record<string, any> = {}): SVGElement {
  const e = document.createElementNS(SVGNS, tag) as SVGElement;
  for (const k in attrs) {
    if (attrs[k] != null) e.setAttribute(k, String(attrs[k]));
  }
  return e;
}

// ---------- shared formatting ----------
export function formatNumber(v: number): string {
  if (v == null || isNaN(v)) return "—";
  const a = Math.abs(v);
  if (a >= 1e9) return (v / 1e9).toFixed(2) + "B";
  if (a >= 1e6) return (v / 1e6).toFixed(1) + "M";
  if (a >= 1e3) return (v / 1e3).toFixed(0) + "K";
  return v.toFixed(0);
}

export function clearSvg(svg: SVGElement) {
  while (svg.firstChild) svg.removeChild(svg.firstChild);
}

// ---------- smooth path (cubic Bezier, axis-locked) ----------
export function smoothPath(points: [number, number][]): string {
  if (!points || points.length < 2) return "";
  let d = `M ${points[0][0].toFixed(2)},${points[0][1].toFixed(2)}`;
  for (let i = 1; i < points.length; i++) {
    const p0 = points[i - 1], p1 = points[i];
    const cpx = (p0[0] + p1[0]) / 2;
    d += ` C ${cpx.toFixed(2)},${p0[1].toFixed(2)} ${cpx.toFixed(2)},${p1[1].toFixed(2)} ${p1[0].toFixed(2)},${p1[1].toFixed(2)}`;
  }
  return d;
}

export function smoothClosedPath(top: [number, number][], bot: [number, number][]): string {
  let d = smoothPath(top);
  if (bot && bot.length) {
    d += ` L ${bot[0][0].toFixed(2)},${bot[0][1].toFixed(2)}`;
    for (let i = 1; i < bot.length; i++) {
      const p0 = bot[i - 1], p1 = bot[i];
      const cpx = (p0[0] + p1[0]) / 2;
      d += ` C ${cpx.toFixed(2)},${p0[1].toFixed(2)} ${cpx.toFixed(2)},${p1[1].toFixed(2)} ${p1[0].toFixed(2)},${p1[1].toFixed(2)}`;
    }
  }
  return d + " Z";
}

// ---------- 1D EMA smoothing ----------
export function ema(vals: number[], alpha = 0.35): number[] {
  if (!vals.length) return vals;
  const out = [vals[0]];
  for (let i = 1; i < vals.length; i++) out.push(alpha * vals[i] + (1 - alpha) * out[i - 1]);
  return out;
}

// ---------- sparkline ----------
export function drawSparkline(svg: SVGElement, vals: number[], direction?: string) {
  clearSvg(svg);
  const W = 200, H = 36, pad = 2;
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  const xStep = (W - pad * 2) / (vals.length - 1);
  const points: [number, number][] = vals.map((v, i) => [pad + i * xStep, H - pad - ((v - min) / span) * (H - pad * 2)]);
  const css = getComputedStyle(document.documentElement);
  const pickVar = (n: string) => (css.getPropertyValue(n) || "").trim();
  const color =
    direction === "bad" ? pickVar("--critical") :
    direction === "warn" ? pickVar("--warning") :
    direction === "good" ? pickVar("--good") :
    pickVar("--accent");

  const id = `sg-${Math.random().toString(36).slice(2, 7)}`;
  const defs = svgEl("defs");
  const lg = svgEl("linearGradient", { id, x1: 0, y1: 0, x2: 0, y2: 1 });
  lg.append(svgEl("stop", { offset: "0", "stop-color": color, "stop-opacity": "0.55" }));
  lg.append(svgEl("stop", { offset: "1", "stop-color": color, "stop-opacity": "0" }));
  defs.append(lg);
  svg.append(defs);

  const areaPath = "M" + points.map((p) => p.join(",")).join(" L ") +
    ` L ${points[points.length - 1][0]},${H} L ${points[0][0]},${H} Z`;
  svg.append(svgEl("path", { d: areaPath, fill: `url(#${id})` }));
  svg.append(svgEl("path", { d: smoothPath(points), fill: "none", stroke: color, "stroke-width": "1.6", "stroke-linejoin": "round" }));
  const last = points[points.length - 1];
  svg.append(svgEl("circle", { cx: last[0], cy: last[1], r: "2.5", fill: color, stroke: "var(--bg-1)", "stroke-width": "1.2" }));
}

// ---------- Forecast fan ----------
export interface FanData {
  labels: string[];
  actuals: number[];
  forecast: number[];
  fc50U: number[]; fc50L: number[];
  fc80U: number[]; fc80L: number[];
  fc95U: number[]; fc95L: number[];
  d365?: number[];
}
export interface FanOpts {
  yLabel?: string;
  zeroBaseline?: boolean;
  unit?: "money" | "raw" | "pct";
}

function fmtAxis(v: number, unit: FanOpts["unit"]): string {
  if (unit === "money") return "$" + formatNumber(v) + (Math.abs(v) >= 1e6 ? "" : "");
  if (unit === "pct") return (v * 100).toFixed(1) + "%";
  return formatNumber(v);
}

export function drawForecastFan(svg: SVGElement, data: FanData, opts: FanOpts = {}) {
  clearSvg(svg);
  const vb = (svg.getAttribute("viewBox") || "0 0 920 240").split(" ");
  const W = parseInt(vb[2], 10) || 920;
  const H = parseInt(vb[3], 10) || 240;
  const margin = { top: 24, right: 24, bottom: 36, left: 56 };
  const innerW = W - margin.left - margin.right;
  const innerH = H - margin.top - margin.bottom;

  const labels = data.labels;
  const actuals = data.actuals;
  const forecast = data.forecast;
  const fc80U = data.fc80U, fc80L = data.fc80L;
  const fc95U = data.fc95U, fc95L = data.fc95L;
  const fc50U = data.fc50U, fc50L = data.fc50L;
  const d365 = data.d365 ?? null;

  const total = actuals.length + forecast.length;
  const xStep = innerW / (total - 1);

  const allVals = [...actuals, ...forecast, ...fc80U, ...fc80L, ...fc95U, ...fc95L, ...(d365 || [])];
  let yMin = Math.min(...allVals);
  let yMax = Math.max(...allVals);
  if (opts.zeroBaseline) yMin = Math.min(yMin, 0);
  const pad = (yMax - yMin) * 0.05 || 1;
  yMin -= pad; yMax += pad;
  const yRange = yMax - yMin || 1;

  const xAt = (i: number) => margin.left + i * xStep;
  const yAt = (v: number) => margin.top + innerH - ((v - yMin) / yRange) * innerH;

  // gridlines
  const ticks = 5;
  for (let t = 0; t <= ticks; t++) {
    const v = yMin + (yRange / ticks) * t;
    const y = yAt(v);
    svg.append(svgEl("line", { x1: margin.left, y1: y, x2: margin.left + innerW, y2: y, class: "grid-line" }));
    const lbl = svgEl("text", { x: margin.left - 8, y: y + 3, "text-anchor": "end" });
    lbl.textContent = fmtAxis(v, opts.unit);
    svg.append(lbl);
  }

  // x labels (every nth)
  const labelEvery = Math.max(1, Math.ceil(total / 12));
  for (let i = 0; i < total; i++) {
    if (i % labelEvery !== 0 && i !== total - 1) continue;
    const t = svgEl("text", { x: xAt(i), y: margin.top + innerH + 20, "text-anchor": "middle" });
    t.textContent = labels[i];
    svg.append(t);
  }

  // forecast bands (95, 80, 50)
  const lastActualIdx = actuals.length - 1;
  const fanXs: number[] = [];
  for (let i = 0; i < forecast.length; i++) fanXs.push(xAt(lastActualIdx + i + 1));

  const bandPath = (upper: number[], lower: number[]): string => {
    const top = upper.map((v, i) => `${fanXs[i]},${yAt(v)}`);
    const bot = lower.map((v, i) => `${fanXs[i]},${yAt(v)}`).reverse();
    const anchor = `${xAt(lastActualIdx)},${yAt(actuals[lastActualIdx])}`;
    return `M ${anchor} L ${top.join(" L ")} L ${bot.join(" L ")} L ${anchor} Z`;
  };
  svg.append(svgEl("path", { d: bandPath(fc95U, fc95L), class: "band-95" }));
  svg.append(svgEl("path", { d: bandPath(fc80U, fc80L), class: "band-80" }));
  svg.append(svgEl("path", { d: bandPath(fc50U, fc50L), class: "band-50" }));

  // zero baseline
  if (opts.zeroBaseline && yMin <= 0 && yMax >= 0) {
    svg.append(svgEl("line", { x1: margin.left, y1: yAt(0), x2: margin.left + innerW, y2: yAt(0), stroke: "var(--axis)", "stroke-width": "1.5" }));
  }

  // actuals line — smoothed
  const actPts: [number, number][] = actuals.map((v, i) => [xAt(i), yAt(v)]);
  svg.append(svgEl("path", { d: smoothPath(actPts), class: "actual-line" }));

  // forecast line (from last actual through forecasts) — smoothed
  const fcPts: [number, number][] = [[xAt(lastActualIdx), yAt(actuals[lastActualIdx])]];
  forecast.forEach((v, i) => fcPts.push([fanXs[i], yAt(v)]));
  svg.append(svgEl("path", { d: smoothPath(fcPts), class: "fc-line" }));

  // d365 overlay
  if (d365) {
    const d365Pts: [number, number][] = [[xAt(lastActualIdx), yAt(actuals[lastActualIdx])]];
    d365.forEach((v, i) => d365Pts.push([fanXs[i], yAt(v)]));
    svg.append(svgEl("path", { d: smoothPath(d365Pts), class: "d365-line" }));
  }

  // points
  actPts.forEach((p) => svg.append(svgEl("circle", { cx: p[0], cy: p[1], r: "2.5", class: "point-actual" })));
  fcPts.slice(1).forEach((p) => svg.append(svgEl("circle", { cx: p[0], cy: p[1], r: "2.5", class: "point-fc" })));

  // now line
  const nowX = xAt(lastActualIdx);
  svg.append(svgEl("line", { x1: nowX, y1: margin.top, x2: nowX, y2: margin.top + innerH, class: "now-line" }));
  const nowLbl = svgEl("text", { x: nowX + 6, y: margin.top + 12, class: "now-label" });
  nowLbl.textContent = "NOW";
  svg.append(nowLbl);

  if (opts.yLabel) {
    const yL = svgEl("text", { x: 14, y: margin.top - 10, fill: "var(--t3)", "font-size": "10" });
    yL.textContent = opts.yLabel;
    svg.append(yL);
  }
}

// ---------- Portfolio mix: stacked area (smoothed) ----------
export interface MixData {
  labels: string[];
  series: { name: string; values: number[]; color: string }[];
}

export function drawStackedArea(svg: SVGElement, data: MixData) {
  clearSvg(svg);
  const vb = (svg.getAttribute("viewBox") || "0 0 460 240").split(" ");
  const W = parseInt(vb[2], 10) || 460;
  const H = parseInt(vb[3], 10) || 240;
  const margin = { top: 56, right: 22, bottom: 34, left: 42 };
  const innerW = W - margin.left - margin.right;
  const innerH = H - margin.top - margin.bottom;
  const n = data.labels.length;
  const xStep = innerW / Math.max(1, n - 1);

  const stacked: number[][][] = [];
  for (let i = 0; i < n; i++) {
    let cum = 0;
    const stack: number[][] = [];
    data.series.forEach((s) => {
      stack.push([cum, cum + s.values[i]]);
      cum += s.values[i];
    });
    stacked.push(stack);
  }
  const yAt = (v: number) => margin.top + innerH - (v / 100) * innerH;
  const xAt = (i: number) => margin.left + i * xStep;

  const legendY = 18;
  let lx = margin.left;
  data.series.forEach((s) => {
    svg.append(svgEl("circle", { cx: lx + 4, cy: legendY, r: 4, fill: s.color, opacity: "0.85" }));
    const t = svgEl("text", { x: lx + 12, y: legendY + 4, fill: "var(--t3)", "font-size": "10.5" });
    t.textContent = s.name;
    svg.append(t);
    lx += s.name.length * 5.8 + 26;
  });

  [0, 25, 50, 75, 100].forEach((v) => {
    svg.append(svgEl("line", { x1: margin.left, y1: yAt(v), x2: margin.left + innerW, y2: yAt(v), class: "grid-line" }));
    const t = svgEl("text", { x: margin.left - 8, y: yAt(v) + 3, "text-anchor": "end" });
    t.textContent = `${v}%`;
    svg.append(t);
  });

  data.series.forEach((s, sIdx) => {
    const top: [number, number][] = [];
    const bot: [number, number][] = [];
    for (let i = 0; i < n; i++) {
      bot.push([xAt(i), yAt(stacked[i][sIdx][0])]);
      top.push([xAt(i), yAt(stacked[i][sIdx][1])]);
    }
    svg.append(svgEl("path", { d: smoothClosedPath(top, bot.slice().reverse()), fill: s.color, opacity: "0.42" }));
    svg.append(svgEl("path", {
      d: smoothPath(top), fill: "none", stroke: s.color, "stroke-width": "1.5",
      "stroke-linecap": "round", "stroke-linejoin": "round", opacity: "0.95"
    }));
  });

  for (let i = 0; i < n; i++) {
    if (i % 2 !== 0 && i !== n - 1) continue;
    const t = svgEl("text", { x: xAt(i), y: margin.top + innerH + 18, "text-anchor": "middle" });
    t.textContent = data.labels[i];
    svg.append(t);
  }

  // Clean Room emphasis annotation if present
  const cleanIdx = data.series.findIndex((s) => /Clean[\s_-]?Room/i.test(s.name));
  if (cleanIdx >= 0) {
    const startVal = data.series[cleanIdx].values[0];
    const endVal = data.series[cleanIdx].values[n - 1];
    const startTopY = yAt(stacked[0][cleanIdx][1]);
    const endTopY = yAt(stacked[n - 1][cleanIdx][1]);

    const startLbl = svgEl("text", { x: xAt(0) + 6, y: startTopY - 6, fill: data.series[cleanIdx].color, "font-size": "11", "font-weight": "600" });
    startLbl.textContent = `${startVal.toFixed(0)}%`;
    svg.append(startLbl);

    const endLbl = svgEl("text", { x: xAt(n - 1) - 6, y: endTopY - 6, fill: data.series[cleanIdx].color, "font-size": "11", "font-weight": "600", "text-anchor": "end" });
    endLbl.textContent = `${endVal.toFixed(0)}%`;
    svg.append(endLbl);

    const ann = svgEl("text", { x: margin.left + innerW, y: 38, "text-anchor": "end", fill: "var(--t2)", "font-size": "11", "font-weight": "500" });
    const tspan1 = svgEl("tspan", { fill: "var(--t3)" }); tspan1.textContent = "Clean Room  ";
    const tspan2 = svgEl("tspan", { fill: data.series[cleanIdx].color, "font-weight": "600" });
    tspan2.textContent = `${startVal.toFixed(0)}% → ${endVal.toFixed(0)}%`;
    ann.append(tspan1, tspan2);
    svg.append(ann);
  }
}

// ---------- Divergence chart (pp from baseline) ----------
export function drawDivergenceChart(svg: SVGElement, data: MixData, baselineIdx = 0) {
  clearSvg(svg);
  const vb = (svg.getAttribute("viewBox") || "0 0 460 240").split(" ");
  const W = parseInt(vb[2], 10) || 460;
  const H = parseInt(vb[3], 10) || 240;
  const margin = { top: 56, right: 56, bottom: 34, left: 44 };
  const innerW = W - margin.left - margin.right;
  const innerH = H - margin.top - margin.bottom;
  const labels = data.labels;
  const n = labels.length;
  const xStep = innerW / Math.max(1, n - 1);
  const bIdx = Math.max(0, Math.min(n - 1, baselineIdx | 0));

  const deltas = data.series.map((s) => ({
    name: s.name,
    color: s.color,
    delta: s.values.map((v) => v - s.values[bIdx]),
  }));

  const allDeltas = deltas.flatMap((d) => d.delta);
  const dMin = Math.min(...allDeltas, 0);
  const dMax = Math.max(...allDeltas, 0);
  const yLo = Math.floor((dMin - 2) / 5) * 5;
  const yHi = Math.ceil((dMax + 2) / 5) * 5;

  const xAt = (i: number) => margin.left + i * xStep;
  const yAt = (v: number) => margin.top + innerH - ((v - yLo) / (yHi - yLo || 1)) * innerH;

  // legend
  let lx = margin.left;
  data.series.forEach((s) => {
    svg.append(svgEl("circle", { cx: lx + 4, cy: 18, r: 4, fill: s.color, opacity: "0.9" }));
    const t = svgEl("text", { x: lx + 12, y: 22, fill: "var(--t3)", "font-size": "10.5" });
    t.textContent = s.name;
    svg.append(t);
    lx += s.name.length * 5.8 + 26;
  });

  const yLabel = svgEl("text", { x: margin.left, y: 40, fill: "var(--t2)", "font-size": "11", "font-weight": "500" });
  yLabel.textContent = `pp change from ${labels[bIdx]}`;
  svg.append(yLabel);

  const tick = 10;
  const start = Math.ceil(yLo / tick) * tick;
  for (let v = start; v <= yHi; v += tick) {
    svg.append(svgEl("line", { x1: margin.left, y1: yAt(v), x2: margin.left + innerW, y2: yAt(v), class: "grid-line" }));
    const t = svgEl("text", { x: margin.left - 8, y: yAt(v) + 3, "text-anchor": "end" });
    t.textContent = v > 0 ? `+${v}` : `${v}`;
    svg.append(t);
  }

  svg.append(svgEl("line", { x1: margin.left, y1: yAt(0), x2: margin.left + innerW, y2: yAt(0), stroke: "var(--axis)", "stroke-width": "1.5", "stroke-linecap": "round" }));

  const baselineX = xAt(bIdx);
  svg.append(svgEl("line", { x1: baselineX, y1: margin.top, x2: baselineX, y2: margin.top + innerH, stroke: "var(--accent-2)", "stroke-width": "1", "stroke-dasharray": "4,3", opacity: "0.85" }));
  const baseLbl = svgEl("text", { x: baselineX + 5, y: margin.top + 12, fill: "var(--accent-2)", "font-family": "var(--mono)", "font-size": "10", "font-weight": "700", "letter-spacing": "0.05em" });
  baseLbl.textContent = "BASELINE";
  svg.append(baseLbl);

  let heroName: string | null = null, heroAbs = -1;
  deltas.forEach((s) => {
    const endAbs = Math.abs(s.delta[s.delta.length - 1]);
    if (endAbs > heroAbs) { heroAbs = endAbs; heroName = s.name; }
  });

  deltas.forEach((s) => {
    const points: [number, number][] = s.delta.map((v, i) => [xAt(i), yAt(v)]);
    const isHero = s.name === heroName;
    svg.append(svgEl("path", { d: smoothPath(points), fill: "none", stroke: s.color,
      "stroke-width": isHero ? "2.6" : "1.6", "stroke-linecap": "round", "stroke-linejoin": "round",
      opacity: isHero ? "1" : "0.6" }));
    const last = points[points.length - 1];
    const lastDelta = s.delta[s.delta.length - 1];
    svg.append(svgEl("circle", { cx: last[0], cy: last[1], r: isHero ? 4 : 3, fill: s.color, stroke: "var(--bg-1)", "stroke-width": "1.5", opacity: isHero ? "1" : "0.7" }));
    const lbl = svgEl("text", { x: last[0] + 8, y: last[1] + 4, fill: s.color, "font-size": isHero ? "11" : "10", "font-weight": isHero ? "700" : "600", opacity: isHero ? "1" : "0.85" });
    lbl.textContent = lastDelta > 0 ? `+${lastDelta.toFixed(0)}pp` : `${lastDelta.toFixed(0)}pp`;
    svg.append(lbl);
  });

  for (let i = 0; i < n; i++) {
    if (i % 2 !== 0 && i !== n - 1) continue;
    const t = svgEl("text", { x: xAt(i), y: margin.top + innerH + 18, "text-anchor": "middle" });
    t.textContent = labels[i];
    svg.append(t);
  }
}

// ---------- DSO stacked area by tier ----------
export interface DsoData {
  months: string[];
  direct: number[];
  ft: number[];
  sts: number[];
}

export function drawDsoStacked(svg: SVGElement, d: DsoData) {
  clearSvg(svg);
  const vb = (svg.getAttribute("viewBox") || "0 0 920 240").split(" ");
  const W = parseInt(vb[2], 10) || 920;
  const H = parseInt(vb[3], 10) || 240;
  const margin = { top: 30, right: 24, bottom: 36, left: 50 };
  const innerW = W - margin.left - margin.right;
  const innerH = H - margin.top - margin.bottom;
  const n = d.months.length;
  const xStep = innerW / Math.max(1, n - 1);
  const totals = d.direct.map((v, i) => v + d.ft[i] + d.sts[i]);
  const yMax = Math.max(...totals) * 1.1 || 1;
  const yAt = (v: number) => margin.top + innerH - (v / yMax) * innerH;
  const xAt = (i: number) => margin.left + i * xStep;

  for (let t = 0; t <= 4; t++) {
    const v = (yMax / 4) * t;
    svg.append(svgEl("line", { x1: margin.left, y1: yAt(v), x2: margin.left + innerW, y2: yAt(v), class: "grid-line" }));
    const lbl = svgEl("text", { x: margin.left - 6, y: yAt(v) + 3, "text-anchor": "end" });
    lbl.textContent = Math.round(v) + "d";
    svg.append(lbl);
  }

  const series = [
    { name: "Direct-to-owner", color: "var(--c-direct)", values: d.direct },
    { name: "First-tier sub", color: "var(--c-ft)", values: d.ft },
    { name: "Sub-to-a-sub", color: "var(--c-sts)", values: d.sts },
  ];
  const stacked: number[][][] = [];
  for (let i = 0; i < n; i++) {
    let cum = 0;
    stacked.push(series.map((s) => { const r = [cum, cum + s.values[i]]; cum += s.values[i]; return r; }));
  }
  series.forEach((s, idx) => {
    const top = stacked.map((arr, i) => [xAt(i), yAt(arr[idx][1])] as [number, number]);
    const bot = stacked.map((arr, i) => [xAt(i), yAt(arr[idx][0])] as [number, number]).reverse();
    svg.append(svgEl("path", { d: smoothClosedPath(top, bot), fill: s.color, opacity: "0.7" }));
    svg.append(svgEl("path", { d: smoothPath(top), fill: "none", stroke: s.color, "stroke-width": "1.2", opacity: "0.95" }));
  });

  const totPts: [number, number][] = totals.map((v, i) => [xAt(i), yAt(v)]);
  svg.append(svgEl("path", { d: smoothPath(totPts), fill: "none", stroke: "var(--t1)", "stroke-width": "2" }));
  totPts.forEach((p) => svg.append(svgEl("circle", { cx: p[0], cy: p[1], r: 3, fill: "var(--t1)", stroke: "var(--bg-1)", "stroke-width": "1.5" })));

  d.months.forEach((m, i) => {
    if (i % Math.max(1, Math.floor(n / 8)) !== 0 && i !== n - 1) return;
    const t = svgEl("text", { x: xAt(i), y: margin.top + innerH + 18, "text-anchor": "middle" });
    t.textContent = m;
    svg.append(t);
  });

  let lx = margin.left;
  const ly = margin.top - 14;
  series.forEach((s) => {
    svg.append(svgEl("rect", { x: lx, y: ly, width: 8, height: 8, fill: s.color, rx: 1.5 }));
    const t = svgEl("text", { x: lx + 12, y: ly + 7 });
    t.textContent = s.name;
    svg.append(t);
    lx += s.name.length * 5.6 + 26;
  });
  svg.append(svgEl("rect", { x: lx, y: ly, width: 8, height: 8, fill: "var(--t1)", rx: 1.5 }));
  const tt = svgEl("text", { x: lx + 12, y: ly + 7, fill: "var(--t1)" });
  tt.textContent = "Enterprise total";
  svg.append(tt);
}

// ---------- AR aging bars ----------
export function drawAgingBars(svg: SVGElement, buckets: Record<string, number>) {
  clearSvg(svg);
  const ORDER = ["0-30", "31-60", "61-90", "91-120", "120+"];
  const COLORS = ["var(--c-direct)", "var(--c-ft)", "var(--c-sts)", "var(--warning)", "var(--critical)"];
  const vb = (svg.getAttribute("viewBox") || "0 0 460 240").split(" ");
  const W = parseInt(vb[2], 10) || 460;
  const H = parseInt(vb[3], 10) || 240;
  const margin = { top: 24, right: 20, bottom: 36, left: 56 };
  const innerW = W - margin.left - margin.right;
  const innerH = H - margin.top - margin.bottom;
  const max = Math.max(...ORDER.map((k) => buckets[k] || 0)) || 1;
  const total = ORDER.reduce((a, k) => a + (buckets[k] || 0), 0);
  const barW = (innerW - (ORDER.length - 1) * 18) / ORDER.length;
  const yAt = (v: number) => margin.top + innerH - (v / max) * innerH;

  [0, 0.25, 0.5, 0.75, 1].forEach((p) => {
    const v = max * p;
    svg.append(svgEl("line", { x1: margin.left, y1: yAt(v), x2: margin.left + innerW, y2: yAt(v), class: "grid-line" }));
    const t = svgEl("text", { x: margin.left - 8, y: yAt(v) + 3, "text-anchor": "end" });
    t.textContent = "$" + formatNumber(v);
    svg.append(t);
  });

  ORDER.forEach((k, i) => {
    const v = buckets[k] || 0;
    const x0 = margin.left + i * (barW + 18);
    const h = innerH * (v / max);
    const pct = total > 0 ? (v / total) * 100 : 0;
    svg.append(svgEl("rect", { x: x0, y: margin.top + innerH - h, width: barW, height: h, rx: 4, fill: COLORS[i], opacity: "0.92" }));
    const lbl = svgEl("text", { x: x0 + barW / 2, y: margin.top + innerH - h - 6, "text-anchor": "middle", fill: "var(--t1)", "font-size": "10.5", "font-weight": "600" });
    lbl.textContent = "$" + formatNumber(v);
    svg.append(lbl);
    const xlbl = svgEl("text", { x: x0 + barW / 2, y: margin.top + innerH + 16, "text-anchor": "middle", fill: "var(--t2)", "font-size": "10.5" });
    xlbl.textContent = k + "d";
    svg.append(xlbl);
    const pctLbl = svgEl("text", { x: x0 + barW / 2, y: margin.top + innerH + 30, "text-anchor": "middle", fill: "var(--t3)", "font-size": "9.5" });
    pctLbl.textContent = pct.toFixed(0) + "%";
    svg.append(pctLbl);
  });
}

// ---------- Tier DSO bars (horizontal) ----------
export function drawTierBars(svg: SVGElement, items: { label: string; value: number; color?: string }[], benchmark?: number) {
  clearSvg(svg);
  const vb = (svg.getAttribute("viewBox") || "0 0 920 240").split(" ");
  const W = parseInt(vb[2], 10) || 920;
  const H = parseInt(vb[3], 10) || 240;
  const margin = { top: 24, right: 60, bottom: 18, left: 160 };
  const innerW = W - margin.left - margin.right;
  const innerH = H - margin.top - margin.bottom;
  const max = Math.max(benchmark || 0, ...items.map((d) => d.value)) * 1.15 || 1;
  const rowH = innerH / items.length;
  const xAt = (v: number) => margin.left + (v / max) * innerW;

  items.forEach((d, i) => {
    const y = margin.top + i * rowH + rowH * 0.18;
    const h = rowH * 0.64;
    const w = (d.value / max) * innerW;
    const lbl = svgEl("text", { x: margin.left - 12, y: y + h / 2 + 4, "text-anchor": "end", fill: "var(--t1)", "font-size": "12", "font-weight": "500" });
    lbl.textContent = d.label;
    svg.append(lbl);
    svg.append(svgEl("rect", { x: margin.left, y, width: innerW, height: h, rx: 4, fill: "var(--surface-3)" }));
    svg.append(svgEl("rect", { x: margin.left, y, width: w, height: h, rx: 4, fill: d.color || "var(--accent)", opacity: "0.92" }));
    const v = svgEl("text", { x: margin.left + w + 8, y: y + h / 2 + 4, "font-size": "12", "font-weight": "600", fill: "var(--t1)", "font-family": "var(--mono)" });
    v.textContent = d.value.toFixed(0) + "d";
    svg.append(v);
  });

  if (benchmark != null) {
    const bx = xAt(benchmark);
    svg.append(svgEl("line", { x1: bx, y1: margin.top - 4, x2: bx, y2: margin.top + innerH, stroke: "var(--warning)", "stroke-width": "1.5", "stroke-dasharray": "3,3" }));
    const bl = svgEl("text", { x: bx, y: margin.top - 8, "text-anchor": "middle", fill: "var(--warning)", "font-size": "9.5" });
    bl.textContent = `benchmark ${benchmark}d`;
    svg.append(bl);
  }
}

// ---------- Peer scatter ----------
export interface PeerPt { x: number; y: number; flagged?: boolean; label?: string }
export function drawPeerScatter(svg: SVGElement, points: PeerPt[]) {
  clearSvg(svg);
  const vb = (svg.getAttribute("viewBox") || "0 0 460 250").split(" ");
  const W = parseInt(vb[2], 10) || 460;
  const H = parseInt(vb[3], 10) || 250;
  const margin = { top: 26, right: 16, bottom: 36, left: 40 };
  const innerW = W - margin.left - margin.right;
  const innerH = H - margin.top - margin.bottom;
  const xAt = (v: number) => margin.left + v * innerW;
  const yAt = (v: number) => margin.top + innerH - v * innerH;

  [0, 0.25, 0.5, 0.75, 1].forEach((v) => {
    svg.append(svgEl("line", { x1: xAt(v), y1: margin.top, x2: xAt(v), y2: margin.top + innerH, class: "grid-line" }));
    svg.append(svgEl("line", { x1: margin.left, y1: yAt(v), x2: margin.left + innerW, y2: yAt(v), class: "grid-line" }));
    const tx = svgEl("text", { x: xAt(v), y: margin.top + innerH + 18, "text-anchor": "middle" });
    tx.textContent = `${(v * 100).toFixed(0)}%`;
    svg.append(tx);
    const ty = svgEl("text", { x: margin.left - 6, y: yAt(v) + 3, "text-anchor": "end" });
    ty.textContent = `${(v * 100).toFixed(0)}%`;
    svg.append(ty);
  });

  svg.append(svgEl("line", { x1: xAt(0), y1: yAt(0), x2: xAt(1), y2: yAt(1), stroke: "var(--accent-2)", "stroke-width": "1", "stroke-dasharray": "4,3", opacity: "0.7" }));

  const band = 0.18;
  svg.append(svgEl("line", { x1: xAt(0), y1: yAt(band), x2: xAt(1 - band), y2: yAt(1), stroke: "var(--axis)", "stroke-width": "1", "stroke-dasharray": "2,3" }));
  svg.append(svgEl("line", { x1: xAt(band), y1: yAt(0), x2: xAt(1), y2: yAt(1 - band), stroke: "var(--axis)", "stroke-width": "1", "stroke-dasharray": "2,3" }));

  points.forEach((p) => {
    svg.append(svgEl("circle", { cx: xAt(p.x), cy: yAt(p.y), r: p.flagged ? 5 : 3,
      fill: p.flagged ? "var(--critical)" : "var(--accent)", "fill-opacity": p.flagged ? "1" : "0.55",
      stroke: p.flagged ? "var(--bg-1)" : "none", "stroke-width": p.flagged ? "1.5" : "0" }));
  });

  const xt = svgEl("text", { x: margin.left + innerW / 2, y: H - 4, "text-anchor": "middle", fill: "var(--t3)", "font-size": "10" });
  xt.textContent = "% Complete (cost-to-cost)";
  svg.append(xt);
  const yt = svgEl("text", { x: 12, y: margin.top + innerH / 2, transform: `rotate(-90 12 ${margin.top + innerH / 2})`, "text-anchor": "middle", fill: "var(--t3)", "font-size": "10" });
  yt.textContent = "% Billed of contract";
  svg.append(yt);
}

// ---------- MSI sparkline ----------
export function drawMsiSparkline(svg: SVGElement, vals: number[]) {
  clearSvg(svg);
  const vb = (svg.getAttribute("viewBox") || "0 0 460 200").split(" ");
  const W = parseInt(vb[2], 10) || 460;
  const H = parseInt(vb[3], 10) || 200;
  const margin = { top: 30, right: 20, bottom: 30, left: 40 };
  const innerW = W - margin.left - margin.right;
  const innerH = H - margin.top - margin.bottom;
  const n = vals.length;
  const xStep = innerW / Math.max(1, n - 1);
  const yMax = Math.max(0.18, ...vals) * 1.05;
  const xAt = (i: number) => margin.left + i * xStep;
  const yAt = (v: number) => margin.top + innerH - (v / yMax) * innerH;

  [0, 0.05, 0.10, 0.15].forEach((v) => {
    if (v > yMax) return;
    svg.append(svgEl("line", { x1: margin.left, y1: yAt(v), x2: margin.left + innerW, y2: yAt(v), class: "grid-line" }));
    const t = svgEl("text", { x: margin.left - 6, y: yAt(v) + 3, "text-anchor": "end" });
    t.textContent = v.toFixed(2);
    svg.append(t);
  });

  if (yMax >= 0.10) {
    svg.append(svgEl("line", { x1: margin.left, y1: yAt(0.10), x2: margin.left + innerW, y2: yAt(0.10), stroke: "var(--warning)", "stroke-width": "1.5", "stroke-dasharray": "5,3", opacity: "0.8" }));
    const thT = svgEl("text", { x: margin.left + innerW - 4, y: yAt(0.10) - 4, "text-anchor": "end", fill: "var(--warning)", "font-size": "10", "font-weight": "600" });
    thT.textContent = "Alert threshold (0.10)";
    svg.append(thT);
  }

  const points: [number, number][] = vals.map((v, i) => [xAt(i), yAt(v)]);
  const id = `msi-grad-${Math.random().toString(36).slice(2, 7)}`;
  const defs = svgEl("defs");
  const lg = svgEl("linearGradient", { id, x1: 0, y1: 0, x2: 0, y2: 1 });
  lg.append(svgEl("stop", { offset: "0", "stop-color": "var(--accent)", "stop-opacity": "0.45" }));
  lg.append(svgEl("stop", { offset: "1", "stop-color": "var(--accent)", "stop-opacity": "0" }));
  defs.append(lg);
  svg.append(defs);

  const areaPath = "M" + points.map((p) => p.join(",")).join(" L ") + ` L ${points[points.length - 1][0]},${margin.top + innerH} L ${points[0][0]},${margin.top + innerH} Z`;
  svg.append(svgEl("path", { d: areaPath, fill: `url(#${id})` }));
  svg.append(svgEl("path", { d: smoothPath(points), fill: "none", stroke: "var(--accent)", "stroke-width": "2" }));

  const last = points[points.length - 1];
  svg.append(svgEl("circle", { cx: last[0], cy: last[1], r: 5, fill: "var(--accent)", stroke: "var(--bg-1)", "stroke-width": "2" }));
  const lastV = svgEl("text", { x: last[0] - 8, y: last[1] - 8, "text-anchor": "end", fill: "var(--t1)", "font-weight": "600", "font-size": "12" });
  lastV.textContent = vals[vals.length - 1].toFixed(3);
  svg.append(lastV);
}

// ---------- SHAP horizontal bars ----------
export interface ShapItem { feature: string; value: number }
export function drawShap(svg: SVGElement, data: ShapItem[]) {
  clearSvg(svg);
  const vb = (svg.getAttribute("viewBox") || "0 0 920 230").split(" ");
  const W = parseInt(vb[2], 10) || 920;
  const H = parseInt(vb[3], 10) || 230;
  const margin = { top: 18, right: 30, bottom: 24, left: 280 };
  const innerW = W - margin.left - margin.right;
  const innerH = H - margin.top - margin.bottom;
  const n = data.length;
  const rowH = innerH / Math.max(1, n);
  const maxAbs = Math.max(...data.map((d) => Math.abs(d.value))) || 1;
  const xCenter = margin.left + innerW / 2;
  const xScale = (innerW / 2) / maxAbs * 0.95;

  svg.append(svgEl("line", { x1: xCenter, y1: margin.top, x2: xCenter, y2: margin.top + innerH, stroke: "var(--axis)", "stroke-width": "1" }));
  [-maxAbs, -maxAbs / 2, 0, maxAbs / 2, maxAbs].forEach((v) => {
    const x = xCenter + v * xScale;
    svg.append(svgEl("line", { x1: x, y1: margin.top, x2: x, y2: margin.top + innerH, class: "grid-line" }));
    const t = svgEl("text", { x, y: margin.top + innerH + 16, "text-anchor": "middle" });
    t.textContent = (v >= 0 ? "+" : "") + v.toFixed(2);
    svg.append(t);
  });

  data.forEach((d, i) => {
    const y = margin.top + i * rowH + rowH / 2;
    const w = Math.abs(d.value) * xScale;
    const x = d.value >= 0 ? xCenter : xCenter - w;
    svg.append(svgEl("rect", { x, y: y - rowH * 0.32, width: w, height: rowH * 0.64,
      fill: d.value >= 0 ? "var(--critical)" : "var(--good)", opacity: "0.75", rx: 2 }));
    const lbl = svgEl("text", { x: margin.left - 12, y: y + 4, "text-anchor": "end", fill: "var(--t1)", "font-size": "12" });
    lbl.textContent = d.feature;
    svg.append(lbl);
    const val = svgEl("text", { x: d.value >= 0 ? x + w + 6 : x - 6, y: y + 4, "text-anchor": d.value >= 0 ? "start" : "end", fill: "var(--t2)", "font-family": "var(--mono)", "font-size": "11" });
    val.textContent = (d.value >= 0 ? "+" : "") + d.value.toFixed(3);
    svg.append(val);
  });

  const lt = svgEl("text", { x: margin.left + 4, y: margin.top - 4, fill: "var(--good)", "font-size": "10", "font-weight": "600" });
  lt.textContent = "← decreases default risk";
  svg.append(lt);
  const rt = svgEl("text", { x: margin.left + innerW - 4, y: margin.top - 4, fill: "var(--critical)", "font-size": "10", "font-weight": "600", "text-anchor": "end" });
  rt.textContent = "increases default risk →";
  svg.append(rt);
}

// ---------- Waterfall ----------
export interface WaterfallSeg { label: string; value: number; type?: "start" | "end" | "pos" | "neg" }
export function drawWaterfall(svg: SVGElement, data: WaterfallSeg[]) {
  clearSvg(svg);
  const vb = (svg.getAttribute("viewBox") || "0 0 920 320").split(" ");
  const W = parseInt(vb[2], 10) || 920;
  const H = parseInt(vb[3], 10) || 320;
  const margin = { top: 30, right: 24, bottom: 70, left: 56 };
  const innerW = W - margin.left - margin.right;
  const innerH = H - margin.top - margin.bottom;
  const n = data.length;
  const barW = Math.max(28, (innerW / Math.max(1, n)) * 0.7);
  const xStep = innerW / Math.max(1, n);

  let running = 0;
  const segs: any[] = [];
  data.forEach((d) => {
    if (d.type === "start") { running = d.value; segs.push({ from: 0, to: d.value, ...d }); }
    else if (d.type === "end") { segs.push({ from: 0, to: d.value, ...d, isEnd: true }); }
    else { segs.push({ from: running, to: running + d.value, ...d }); running += d.value; }
  });

  const allVals = segs.flatMap((s) => [s.from, s.to]);
  const yMin = Math.min(...allVals, 0) * 0.95;
  const yMax = Math.max(...allVals) * 1.05 || 1;
  const yAt = (v: number) => margin.top + innerH - ((v - yMin) / (yMax - yMin)) * innerH;

  const ticks = 5;
  for (let t = 0; t <= ticks; t++) {
    const v = yMin + ((yMax - yMin) / ticks) * t;
    svg.append(svgEl("line", { x1: margin.left, y1: yAt(v), x2: margin.left + innerW, y2: yAt(v), class: "grid-line" }));
    const lbl = svgEl("text", { x: margin.left - 6, y: yAt(v) + 3, "text-anchor": "end" });
    lbl.textContent = "$" + formatNumber(v);
    svg.append(lbl);
  }

  segs.forEach((s, i) => {
    const x = margin.left + xStep * i + (xStep - barW) / 2;
    const yTop = yAt(Math.max(s.from, s.to));
    const yBot = yAt(Math.min(s.from, s.to));
    const h = Math.max(2, yBot - yTop);
    let fill = "var(--accent)";
    if (s.type === "start" || s.type === "end") fill = "var(--accent)";
    else if (s.type === "neg") fill = "var(--critical)";
    else if (s.type === "pos") fill = "var(--good)";
    svg.append(svgEl("rect", { x, y: yTop, width: barW, height: h, fill, opacity: s.type === "start" || s.type === "end" ? "0.9" : "0.75", rx: 2 }));

    const lbl = svgEl("text", { x: x + barW / 2, y: yTop - 5, "text-anchor": "middle", fill: "var(--t1)", "font-weight": "600", "font-size": "11" });
    lbl.textContent = (s.type === "neg" ? "" : (s.type === "pos" ? "+" : "")) + "$" + formatNumber(s.value);
    svg.append(lbl);

    const cat = svgEl("text", { x: x + barW / 2, y: margin.top + innerH + 16, "text-anchor": "end", fill: "var(--t3)", "font-size": "10",
      transform: `rotate(-30 ${x + barW / 2} ${margin.top + innerH + 16})` });
    cat.textContent = s.label;
    svg.append(cat);

    if (i < segs.length - 1) {
      const next = segs[i + 1];
      if (next.type !== "end" && s.type !== "end") {
        const xNext = margin.left + xStep * (i + 1) + (xStep - barW) / 2;
        svg.append(svgEl("line", { x1: x + barW, y1: yAt(s.to), x2: xNext, y2: yAt(s.to), stroke: "var(--axis)", "stroke-width": "1", "stroke-dasharray": "2,3" }));
      }
    }
  });
}

// ---------- Pay-cycle boxplots (small multiples per tier) ----------
export interface BoxplotItem {
  tier: string;
  color: string;
  median: number;
  q1: number;
  q3: number;
  min: number;
  max: number;
  baseline: number;
}
export function drawPayCycleBoxplots(svg: SVGElement, data: BoxplotItem[]) {
  clearSvg(svg);
  const vb = (svg.getAttribute("viewBox") || "0 0 460 240").split(" ");
  const W = parseInt(vb[2], 10) || 460;
  const H = parseInt(vb[3], 10) || 240;
  const margin = { top: 24, right: 20, bottom: 56, left: 40 };
  const innerW = W - margin.left - margin.right;
  const innerH = H - margin.top - margin.bottom;

  const allMin = Math.min(...data.map((d) => d.min));
  const allMax = Math.max(...data.map((d) => d.max));
  const yPad = (allMax - allMin) * 0.1;
  const yMin = Math.max(0, allMin - yPad);
  const yMax = allMax + yPad;
  const yAt = (v: number) => margin.top + innerH - ((v - yMin) / (yMax - yMin || 1)) * innerH;
  const groupW = innerW / data.length;

  [0, 0.25, 0.5, 0.75, 1].forEach((p) => {
    const v = yMin + p * (yMax - yMin);
    svg.append(svgEl("line", { x1: margin.left, y1: yAt(v), x2: margin.left + innerW, y2: yAt(v), class: "grid-line" }));
    const t = svgEl("text", { x: margin.left - 6, y: yAt(v) + 3, "text-anchor": "end" });
    t.textContent = Math.round(v) + "d";
    svg.append(t);
  });

  data.forEach((d, i) => {
    const cx = margin.left + groupW * i + groupW / 2;
    const boxW = Math.min(72, groupW * 0.5);

    // baseline marker
    svg.append(svgEl("line", {
      x1: cx - groupW / 2 + 8, y1: yAt(d.baseline),
      x2: cx + groupW / 2 - 8, y2: yAt(d.baseline),
      stroke: d.color, "stroke-width": "1.5", "stroke-dasharray": "4,3", opacity: "0.6",
    }));
    const blbl = svgEl("text", {
      x: cx + groupW / 2 - 10, y: yAt(d.baseline) - 4,
      "text-anchor": "end", fill: d.color, "font-size": "10",
    });
    blbl.textContent = `baseline ${d.baseline}d`;
    svg.append(blbl);

    // whiskers
    svg.append(svgEl("line", { x1: cx, y1: yAt(d.min), x2: cx, y2: yAt(d.max), stroke: d.color, "stroke-width": "1.5", opacity: "0.8" }));
    svg.append(svgEl("line", { x1: cx - boxW / 3, y1: yAt(d.min), x2: cx + boxW / 3, y2: yAt(d.min), stroke: d.color, "stroke-width": "1.5" }));
    svg.append(svgEl("line", { x1: cx - boxW / 3, y1: yAt(d.max), x2: cx + boxW / 3, y2: yAt(d.max), stroke: d.color, "stroke-width": "1.5" }));

    // box (Q1 → Q3)
    svg.append(svgEl("rect", {
      x: cx - boxW / 2, y: yAt(d.q3),
      width: boxW, height: yAt(d.q1) - yAt(d.q3),
      fill: d.color, opacity: "0.35", stroke: d.color, "stroke-width": "1.2",
    }));

    // median
    svg.append(svgEl("line", {
      x1: cx - boxW / 2, y1: yAt(d.median), x2: cx + boxW / 2, y2: yAt(d.median),
      stroke: d.color, "stroke-width": "2.5",
    }));

    // tier label + median value
    const lbl1 = svgEl("text", { x: cx, y: margin.top + innerH + 18, "text-anchor": "middle", fill: "var(--t1)", "font-weight": "500", "font-size": "11" });
    lbl1.textContent = d.tier;
    svg.append(lbl1);
    const lbl2 = svgEl("text", { x: cx, y: margin.top + innerH + 32, "text-anchor": "middle", fill: "var(--t3)", "font-size": "10" });
    lbl2.textContent = `median ${d.median}d · IQR ${d.q1}–${d.q3}`;
    svg.append(lbl2);
  });
}

// ---------- CPI / SPI scatter (project performance) ----------
export interface CpiSpiPoint {
  cpi: number;
  spi: number;
  contract: number;
  label: string;
}

export function drawCpiSpiScatter(svg: SVGElement, points: CpiSpiPoint[], opts: { sigma?: number } = {}) {
  clearSvg(svg);
  const vb = (svg.getAttribute("viewBox") || "0 0 920 240").split(" ");
  const W = parseInt(vb[2], 10) || 920;
  const H = parseInt(vb[3], 10) || 240;
  const margin = { top: 20, right: 70, bottom: 28, left: 50 };
  const innerW = W - margin.left - margin.right;
  const innerH = H - margin.top - margin.bottom;
  const sigma = opts.sigma ?? 0.10;

  const xMin = 0.5, xMax = 1.5, yMin = 0.5, yMax = 1.5;
  const xAt = (v: number) => margin.left + ((v - xMin) / (xMax - xMin)) * innerW;
  const yAt = (v: number) => margin.top + innerH - ((v - yMin) / (yMax - yMin)) * innerH;
  const cx = xAt(1), cy = yAt(1);

  // Quadrant tints — top-right (good) and bottom-left (critical)
  svg.append(svgEl("rect", {
    x: cx, y: margin.top, width: margin.left + innerW - cx, height: cy - margin.top,
    fill: "var(--good)", "fill-opacity": "0.05",
  }));
  svg.append(svgEl("rect", {
    x: margin.left, y: cy, width: cx - margin.left, height: margin.top + innerH - cy,
    fill: "var(--critical)", "fill-opacity": "0.06",
  }));

  // Gridlines + tick labels at 0.6, 0.8, 1.0, 1.2, 1.4
  [0.6, 0.8, 1.0, 1.2, 1.4].forEach((v) => {
    svg.append(svgEl("line", { x1: xAt(v), y1: margin.top, x2: xAt(v), y2: margin.top + innerH,
      class: "grid-line", "stroke-opacity": v === 1 ? "0" : "1" }));
    svg.append(svgEl("line", { x1: margin.left, y1: yAt(v), x2: margin.left + innerW, y2: yAt(v),
      class: "grid-line", "stroke-opacity": v === 1 ? "0" : "1" }));
    const tx = svgEl("text", { x: xAt(v), y: margin.top + innerH + 16, "text-anchor": "middle" });
    tx.textContent = v.toFixed(1);
    svg.append(tx);
    const ty = svgEl("text", { x: margin.left - 8, y: yAt(v) + 3, "text-anchor": "end" });
    ty.textContent = v.toFixed(1);
    svg.append(ty);
  });

  // 1σ and 2σ envelopes around (1, 1)
  svg.append(svgEl("ellipse", {
    cx, cy,
    rx: (sigma * innerW) / (xMax - xMin),
    ry: (sigma * innerH) / (yMax - yMin),
    fill: "none", stroke: "var(--accent)", "stroke-dasharray": "3 3", "stroke-width": "1", opacity: "0.55",
  }));
  svg.append(svgEl("ellipse", {
    cx, cy,
    rx: (2 * sigma * innerW) / (xMax - xMin),
    ry: (2 * sigma * innerH) / (yMax - yMin),
    fill: "none", stroke: "var(--warning)", "stroke-dasharray": "3 3", "stroke-width": "1", opacity: "0.45",
  }));

  // Crosshair at (1, 1)
  svg.append(svgEl("line", { x1: cx, y1: margin.top, x2: cx, y2: margin.top + innerH,
    stroke: "var(--axis)", "stroke-width": "1.2" }));
  svg.append(svgEl("line", { x1: margin.left, y1: cy, x2: margin.left + innerW, y2: cy,
    stroke: "var(--axis)", "stroke-width": "1.2" }));

  // Plot points — convention: x = CPI (cost), y = SPI (schedule).
  const maxContract = Math.max(1, ...points.map((p) => p.contract));
  points.forEach((p) => {
    const cpiClamped = Math.max(xMin, Math.min(xMax, p.cpi));
    const spiClamped = Math.max(yMin, Math.min(yMax, p.spi));
    const x = xAt(cpiClamped);
    const y = yAt(spiClamped);
    const r = 2 + Math.sqrt(p.contract / maxContract) * 7;
    const dist = Math.sqrt((p.cpi - 1) ** 2 + (p.spi - 1) ** 2);
    const fill =
      p.cpi >= 1 && p.spi >= 1 ? "var(--good)" :
      p.cpi < 1 && p.spi < 1   ? "var(--critical)" :
      "var(--accent)";
    const isOutlier = dist > 2 * sigma;
    svg.append(svgEl("circle", {
      cx: x, cy: y, r,
      fill,
      "fill-opacity": isOutlier ? "0.85" : "0.55",
      stroke: isOutlier ? "var(--bg-1)" : "none",
      "stroke-width": isOutlier ? "1.5" : "0",
    }));
  });

  // Axis labels (corners)
  const tag = (x: number, y: number, text: string, color: string) => {
    const t = svgEl("text", { x, y, "text-anchor": "middle", fill: color,
      "font-size": "9.5", "font-weight": "600", opacity: "0.85" });
    t.textContent = text;
    svg.append(t);
  };
  tag(xAt(1.32), yAt(1.42), "AHEAD · UNDER BUDGET", "var(--good)");
  tag(xAt(0.68), yAt(1.42), "BEHIND · UNDER BUDGET", "var(--t3)");
  tag(xAt(1.32), yAt(0.58), "AHEAD · OVER BUDGET", "var(--t3)");
  tag(xAt(0.68), yAt(0.58), "BEHIND · OVER BUDGET", "var(--critical)");

  // Axis titles
  const xt = svgEl("text", { x: margin.left + innerW / 2, y: H - 4, "text-anchor": "middle",
    fill: "var(--t3)", "font-size": "10" });
  xt.textContent = "SPI — schedule performance";
  svg.append(xt);
  const yt = svgEl("text", {
    x: 12, y: margin.top + innerH / 2,
    transform: `rotate(-90 12 ${margin.top + innerH / 2})`,
    "text-anchor": "middle", fill: "var(--t3)", "font-size": "10",
  });
  yt.textContent = "CPI — cost performance";
  svg.append(yt);

  // Legend (right side): σ envelopes
  const lx = margin.left + innerW + 8;
  const lTop = margin.top + 4;
  const dot = (yOff: number, color: string, label: string, dashed = false) => {
    if (dashed) {
      svg.append(svgEl("line", { x1: lx, y1: lTop + yOff, x2: lx + 14, y2: lTop + yOff,
        stroke: color, "stroke-dasharray": "3 3", "stroke-width": "1.2" }));
    } else {
      svg.append(svgEl("circle", { cx: lx + 6, cy: lTop + yOff, r: 4, fill: color, "fill-opacity": "0.7" }));
    }
    const t = svgEl("text", { x: lx + 18, y: lTop + yOff + 3, "font-size": "9.5", fill: "var(--t3)" });
    t.textContent = label;
    svg.append(t);
  };
  dot(0,  "var(--accent)",  "1σ band", true);
  dot(14, "var(--warning)", "2σ band", true);
  dot(30, "var(--good)",     "in control");
  dot(44, "var(--accent)",   "watchlist");
  dot(58, "var(--critical)", "escalate");
}

// ---------- Pay-cycle history (smooth trend with baseline) ----------
export function drawPayCycleHistory(svg: SVGElement, payCycle: { months: string[]; values: number[] } | null | undefined) {
  clearSvg(svg);
  const vb = (svg.getAttribute("viewBox") || "0 0 460 230").split(" ");
  const W = parseInt(vb[2], 10) || 460;
  const H = parseInt(vb[3], 10) || 230;
  const margin = { top: 18, right: 14, bottom: 26, left: 36 };
  const innerW = W - margin.left - margin.right;
  const innerH = H - margin.top - margin.bottom;

  if (!payCycle || !payCycle.values || payCycle.values.length === 0) {
    const t = svgEl("text", { x: W / 2, y: H / 2, "text-anchor": "middle", fill: "var(--t4)", "font-size": "12" });
    t.textContent = "Pay-cycle history not available.";
    svg.append(t);
    return;
  }

  const vals = payCycle.values;
  const months = payCycle.months;
  const n = vals.length;
  const xStep = innerW / Math.max(1, n - 1);
  const yMin = Math.floor(Math.min(...vals) * 0.9);
  const yMax = Math.ceil(Math.max(...vals) * 1.05);
  const yAt = (v: number) => margin.top + innerH - ((v - yMin) / (yMax - yMin || 1)) * innerH;
  const xAt = (i: number) => margin.left + i * xStep;

  const baseline = vals.slice(0, Math.min(6, vals.length)).reduce((a, b) => a + b, 0) / Math.min(6, vals.length);
  svg.append(svgEl("line", { x1: margin.left, y1: yAt(baseline), x2: margin.left + innerW, y2: yAt(baseline), stroke: "var(--t4)", "stroke-width": "1", "stroke-dasharray": "3,3", opacity: "0.7" }));
  const blbl = svgEl("text", { x: margin.left + innerW - 4, y: yAt(baseline) - 4, "text-anchor": "end", fill: "var(--t4)", "font-size": "9.5" });
  blbl.textContent = `baseline ${Math.round(baseline)}d`;
  svg.append(blbl);

  [yMin, (yMin + yMax) / 2, yMax].forEach((v) => {
    svg.append(svgEl("line", { x1: margin.left, y1: yAt(v), x2: margin.left + innerW, y2: yAt(v), class: "grid-line" }));
    const t = svgEl("text", { x: margin.left - 6, y: yAt(v) + 3, "text-anchor": "end" });
    t.textContent = Math.round(v) + "d";
    svg.append(t);
  });

  const points: [number, number][] = vals.map((v, i) => [xAt(i), yAt(v)]);
  svg.append(svgEl("path", { d: smoothPath(points), fill: "none", stroke: "var(--accent)", "stroke-width": "2", "stroke-linecap": "round" }));
  const last = points[points.length - 1];
  svg.append(svgEl("circle", { cx: last[0], cy: last[1], r: 4, fill: "var(--accent)", stroke: "var(--bg-1)", "stroke-width": "1.5" }));
  const lastV = svgEl("text", { x: last[0] - 6, y: last[1] - 6, "text-anchor": "end", fill: "var(--t1)", "font-size": "10", "font-weight": "600" });
  lastV.textContent = `${Math.round(vals[vals.length - 1])}d`;
  svg.append(lastV);

  [0, Math.floor(n / 3), Math.floor((2 * n) / 3), n - 1].forEach((i) => {
    if (i < 0 || i >= n) return;
    const t = svgEl("text", { x: xAt(i), y: margin.top + innerH + 16, "text-anchor": "middle" });
    t.textContent = months[i];
    svg.append(t);
  });
}

// ---------- News sentiment timeline (volume × sentiment) ----------
export interface SentimentBucket {
  label: string;        // week label (e.g., "W14")
  positive: number;     // count
  neutral: number;
  negative: number;
  netSentiment: number; // weighted: -1..+1
}
export function drawSentimentTimeline(svg: SVGElement, buckets: SentimentBucket[]) {
  clearSvg(svg);
  const vb = (svg.getAttribute("viewBox") || "0 0 460 200").split(" ");
  const W = parseInt(vb[2], 10) || 460;
  const H = parseInt(vb[3], 10) || 200;
  const margin = { top: 20, right: 14, bottom: 30, left: 36 };
  const innerW = W - margin.left - margin.right;
  const innerH = H - margin.top - margin.bottom;
  const n = buckets.length;
  if (n === 0) return;
  const xStep = innerW / Math.max(1, n - 1);
  const xAt = (i: number) => margin.left + i * xStep;
  const maxStack = Math.max(1, ...buckets.map((b) => b.positive + b.neutral + b.negative));
  const yStackAt = (cum: number) => margin.top + innerH - (cum / maxStack) * innerH * 0.55;

  // Bottom-half: stacked volume bars (positive top, neutral mid, negative bottom-ish)
  const bw = Math.max(2, xStep * 0.55);
  buckets.forEach((b, i) => {
    const x = xAt(i) - bw / 2;
    const baseY = margin.top + innerH;
    const negH = (b.negative / maxStack) * innerH * 0.55;
    const neuH = (b.neutral / maxStack) * innerH * 0.55;
    const posH = (b.positive / maxStack) * innerH * 0.55;
    svg.append(svgEl("rect", { x, y: baseY - negH, width: bw, height: negH, fill: "var(--critical)", "fill-opacity": "0.75", rx: 1 }));
    svg.append(svgEl("rect", { x, y: baseY - negH - neuH, width: bw, height: neuH, fill: "var(--c5)", "fill-opacity": "0.6", rx: 1 }));
    svg.append(svgEl("rect", { x, y: baseY - negH - neuH - posH, width: bw, height: posH, fill: "var(--good)", "fill-opacity": "0.78", rx: 1 }));
    void yStackAt;
  });

  // Top half: net-sentiment line (-1..+1 mapped to upper portion)
  const sentH = innerH * 0.4;
  const sentMid = margin.top + sentH / 2;
  const yNet = (v: number) => sentMid - (v * sentH) / 2;
  // Center axis
  svg.append(svgEl("line", { x1: margin.left, y1: sentMid, x2: margin.left + innerW, y2: sentMid,
    stroke: "var(--axis)", "stroke-width": "1", "stroke-dasharray": "2 3" }));

  const points: [number, number][] = buckets.map((b, i) => [xAt(i), yNet(b.netSentiment)]);
  svg.append(svgEl("path", { d: smoothPath(points), fill: "none", stroke: "var(--accent)", "stroke-width": "2", "stroke-linecap": "round" }));
  points.forEach((p, i) => {
    const v = buckets[i].netSentiment;
    const fill = v > 0.15 ? "var(--good)" : v < -0.15 ? "var(--critical)" : "var(--accent)";
    svg.append(svgEl("circle", { cx: p[0], cy: p[1], r: 3, fill, stroke: "var(--bg-1)", "stroke-width": "1" }));
  });

  // Labels
  const sentTop = svgEl("text", { x: margin.left - 6, y: yNet(1) + 3, "text-anchor": "end" });
  sentTop.textContent = "+1";
  svg.append(sentTop);
  const sentZero = svgEl("text", { x: margin.left - 6, y: sentMid + 3, "text-anchor": "end" });
  sentZero.textContent = "0";
  svg.append(sentZero);
  const sentBot = svgEl("text", { x: margin.left - 6, y: yNet(-1) + 3, "text-anchor": "end" });
  sentBot.textContent = "−1";
  svg.append(sentBot);

  // Volume y-tick at top of bars
  const vt = svgEl("text", { x: margin.left - 6, y: margin.top + innerH + 3, "text-anchor": "end" });
  vt.textContent = "0";
  svg.append(vt);
  const vt2 = svgEl("text", { x: margin.left - 6, y: margin.top + innerH - innerH * 0.55 + 3, "text-anchor": "end" });
  vt2.textContent = String(maxStack);
  svg.append(vt2);

  // x labels (every 2nd)
  for (let i = 0; i < n; i++) {
    if (i % 2 !== 0 && i !== n - 1) continue;
    const t = svgEl("text", { x: xAt(i), y: margin.top + innerH + 18, "text-anchor": "middle" });
    t.textContent = buckets[i].label;
    svg.append(t);
  }
}

// ---------- Diverging horizontal bar list ----------
export interface DivergingItem { label: string; value: number; sub?: string }
export function drawDivergingBars(svg: SVGElement, data: DivergingItem[]) {
  clearSvg(svg);
  const vb = (svg.getAttribute("viewBox") || "0 0 920 240").split(" ");
  const W = parseInt(vb[2], 10) || 920;
  const H = parseInt(vb[3], 10) || 240;
  const margin = { top: 18, right: 100, bottom: 22, left: 220 };
  const innerW = W - margin.left - margin.right;
  const innerH = H - margin.top - margin.bottom;
  const n = data.length;
  const rowH = innerH / Math.max(1, n);
  const maxAbs = Math.max(...data.map((d) => Math.abs(d.value))) || 1;
  const xCenter = margin.left + innerW / 2;
  const xScale = (innerW / 2) / maxAbs * 0.95;

  svg.append(svgEl("line", { x1: xCenter, y1: margin.top, x2: xCenter, y2: margin.top + innerH, stroke: "var(--axis)", "stroke-width": "1" }));

  data.forEach((d, i) => {
    const y = margin.top + i * rowH + rowH / 2;
    const w = Math.abs(d.value) * xScale;
    const x = d.value >= 0 ? xCenter : xCenter - w;
    svg.append(svgEl("rect", { x, y: y - rowH * 0.30, width: w, height: rowH * 0.6, fill: d.value >= 0 ? "var(--good)" : "var(--critical)", opacity: "0.78", rx: 3 }));
    const lbl = svgEl("text", { x: margin.left - 10, y: y + 4, "text-anchor": "end", fill: "var(--t1)", "font-size": "12" });
    lbl.textContent = d.label;
    svg.append(lbl);
    if (d.sub) {
      const s = svgEl("text", { x: margin.left - 10, y: y + 16, "text-anchor": "end", fill: "var(--t4)", "font-size": "10" });
      s.textContent = d.sub;
      svg.append(s);
    }
    const val = svgEl("text", { x: d.value >= 0 ? x + w + 6 : x - 6, y: y + 4, "text-anchor": d.value >= 0 ? "start" : "end", fill: d.value >= 0 ? "var(--good)" : "var(--critical)", "font-family": "var(--mono)", "font-size": "11.5", "font-weight": "600" });
    val.textContent = (d.value >= 0 ? "+$" : "−$") + formatNumber(Math.abs(d.value));
    svg.append(val);
  });
}
